import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { QueryError, retryTransient } from "@/lib/queryState";
import { Button, Icon, Panel, Skeleton, timeET, etSyd } from "@/sentinel";

// Validation family for check states; an expired evidence window is a provenance (staleness) state.
const TONES = { blocked: "var(--validation-fail)", unknown: "var(--validation-unconfirmed)", clear: "var(--validation-pass)", expired: "var(--provenance)" };
const ICONS = { blocked: "OctagonX", unknown: "CircleHelp", clear: "CircleCheck", expired: "Hourglass" };
const LABELS = { blocked: "BLOCKED", unknown: "UNKNOWN", clear: "CLEAR", expired: "EXPIRED" };
const at = (value) => (value ? etSyd(value) : "not recorded");

const ReadinessItem = ({ item }) => {
  const expired = item.display_status === "expired";
  const key = expired ? "expired" : item.status;
  return (
    <li data-testid={`entry-readiness-item-${item.id}`}>
      <Icon name={ICONS[key] || ICONS.unknown} size={18} style={{ color: TONES[key] }} label={LABELS[key] || item.status} />
      <span style={{ fontWeight: 500, color: item.status === "clear" ? "var(--text-secondary)" : "var(--text-primary)", overflowWrap: "anywhere" }} data-testid={`entry-readiness-title-${item.id}`}>{item.title}</span>
      <span className="sn-micro" style={{ color: TONES[key], whiteSpace: "nowrap" }} data-testid={`entry-readiness-state-${item.id}`} data-underlying-status={item.status}>{LABELS[key] || item.status}</span>
      <span className="sn-ready-d" style={{ marginTop: 0, overflowWrap: "anywhere", color: "var(--text-secondary)" }} data-testid={`entry-readiness-detail-${item.id}`}>{item.detail}</span>
      {item.resolution && <span className="sn-ready-d" style={{ marginTop: 0, overflowWrap: "anywhere" }} data-testid={`entry-readiness-resolution-${item.id}`}><span className="sn-faint">Next: </span><span style={{ color: "var(--text-primary)" }}>{item.resolution}</span></span>}
    </li>
  );
};

const ReadinessSection = ({ title, subtitle, items, showClear, id }) => {
  const visible = items.filter((item) => showClear || item.status !== "clear");
  const clearCount = items.filter((item) => item.status === "clear").length;
  return (
    <div style={{ minWidth: 0 }} data-testid={`entry-readiness-${id}`}>
      <h3 className="sn-micro" style={{ margin: 0 }} data-testid={`entry-readiness-${id}-title`}>{title}</h3>
      <div className="sn-small sn-faint" style={{ margin: "var(--space-1) 0" }} data-testid={`entry-readiness-${id}-time`}>{subtitle}</div>
      <ul className="sn-ready">{visible.map((item) => <ReadinessItem key={item.id} item={item} />)}</ul>
      {!showClear && clearCount > 0 && <div className="sn-small sn-faint" style={{ marginTop: "var(--space-2)" }} data-testid={`entry-readiness-${id}-clear-count`}>{clearCount} clear hidden</div>}
    </div>
  );
};

/**
 * Entry readiness: collapsed to one summary line by default, expandable to every check.
 * Polls only while `live` (today's session during market hours).
 */
export const EntryReadinessChecklist = ({ date, run, wide = false, live = false, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [showClear, setShowClear] = useState(false);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!live) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(timer);
  }, [live]);
  const query = useQuery({
    queryKey: ["entry-readiness", date, run], queryFn: () => api.getEntryReadiness(date, run),
    enabled: !!date, refetchInterval: live ? 20000 : false, retry: retryTransient,
  });
  const { data, isLoading, isFetching, dataUpdatedAt, refetch } = query;
  const elapsed = Math.max(0, now - dataUpdatedAt);
  const expired = data?.evidence_expires_at && Date.parse(data.checked_at) + elapsed > Date.parse(data.evidence_expires_at);
  const evidence = (data?.frozen_evidence || []).map((item) => item.id === "checkpoint" && item.status === "clear" && expired ? {
    ...item, status: "blocked", display_status: "expired", detail: "Evidence window closed; this checkpoint is history.", resolution: null,
  } : item.display_status === "expired" ? { ...item, detail: "Evidence window closed; this checkpoint is history.", resolution: null } : item);
  const controls = data?.current_controls || [];
  const items = [...controls, ...evidence];
  const expiredCount = evidence.filter((item) => item.display_status === "expired").length;
  const blocked = items.filter((item) => item.status === "blocked").length;
  const unknown = items.filter((item) => item.status === "unknown").length;
  const clearTotal = items.filter((item) => item.status === "clear").length;
  const status = !data ? "unknown" : blocked ? "blocked" : unknown ? "unknown" : "clear";

  const summary = !data ? (isLoading ? "Checking…" : "Unavailable")
    : [blocked - expiredCount > 0 && `${blocked - expiredCount} blocked`, expiredCount > 0 && `${expiredCount} expired`, unknown > 0 && `${unknown} unknown`, `${clearTotal} clear`].filter(Boolean).join(" · ");

  return (
    <Panel
      title={<span data-testid="entry-readiness-title">Entry readiness</span>}
      icon="ListChecks"
      aria-label="Entry readiness checklist"
      data-testid="entry-readiness-panel"
      meta={
        <span className="sn-row" style={{ gap: 6, flexWrap: "nowrap", color: TONES[status], fontWeight: 500 }} data-testid="entry-readiness-status-badge">
          <Icon name={ICONS[status]} size={14} />{LABELS[status]}
        </span>
      }
      actions={data && (
        <Button size="sm" variant="ghost" icon={open ? "ChevronUp" : "ChevronDown"} aria-expanded={open} onClick={() => setOpen((o) => !o)} data-testid="entry-readiness-expand">
          {open ? "Hide checks" : "Show checks"}
        </Button>
      )}
      style={{ minWidth: 0 }}
    >
      <div className="sn-stack" data-testid="entry-readiness-header" style={{ gap: "var(--space-3)" }}>
        {!date ? (
          <p className="sn-small sn-muted" style={{ margin: 0 }} data-testid="entry-readiness-notice">Select a session to inspect its entry checks.</p>
        ) : isLoading ? (
          <Skeleton lines={2} />
        ) : !data ? (
          <QueryError query={query} title="Entry readiness could not load" what="the entry checks" testId="entry-readiness-notice" />
        ) : (
          <>
            <p className="sn-small sn-muted" style={{ margin: 0 }} data-testid="entry-readiness-context">
              <span style={{ color: "var(--text-primary)", fontWeight: 500 }}>{summary}</span>
              {" "}· {data.selected_run ? `Run ${data.selected_run}` : "no entry checkpoint yet"} · checked <span className="sn-num">{at(data.checked_at)}</span> · read-only
            </p>
            {query.isError && <QueryError query={query} title="Refresh failed · showing the last good checks" what="the entry checks" />}
            {open && (
              <>
                <div className="sn-grid-12" style={{ gap: "var(--space-4) var(--space-6)" }}>
                  <div className={wide ? "sn-col-6" : "sn-col-12"}><ReadinessSection id="current-controls" title="Current controls" subtitle={`Checked ${at(data.checked_at)}`} items={controls} showClear={showClear} /></div>
                  <div className={wide ? "sn-col-6" : "sn-col-12"}><ReadinessSection id="frozen-evidence" title="Checkpoint evidence (frozen)" subtitle={`Evaluated ${at(data.checkpoint_at)}`} items={evidence} showClear={showClear} /></div>
                </div>
                <div className="sn-row">
                  <Button size="sm" icon="RefreshCw" loading={isFetching} onClick={() => refetch()} data-testid="entry-readiness-refresh">
                    {isFetching ? "Checking" : "Refresh checks"}
                  </Button>
                  <Button size="sm" variant="ghost" aria-expanded={showClear} onClick={() => setShowClear(!showClear)} data-testid="entry-readiness-toggle-clear">
                    {showClear ? "Hide clear checks" : <>Show clear checks (<span className="sn-num">{clearTotal}</span>)</>}
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Panel>
  );
};
